import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Users,
  Download,
  Search,
  Loader2,
  ChevronDown,
  ChevronUp,
  FileText,
  Building2,
  Filter
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const getStatusBadge = (status) => {
  switch (status) {
    case "registered":
      return <span className="inline-flex items-center px-1.5 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs font-mono uppercase">Registered</span>;
    case "examination":
      return <span className="inline-flex items-center px-1.5 py-0.5 bg-blue-100 text-blue-700 border border-blue-200 text-xs font-mono uppercase">Examination</span>;
    case "published":
      return <span className="inline-flex items-center px-1.5 py-0.5 bg-purple-100 text-purple-700 border border-purple-200 text-xs font-mono uppercase">Published</span>;
    case "opposed":
      return <span className="inline-flex items-center px-1.5 py-0.5 bg-orange-100 text-orange-700 border border-orange-200 text-xs font-mono uppercase">Opposed</span>;
    case "dead":
      return <span className="inline-flex items-center px-1.5 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 text-xs font-mono uppercase">Dead</span>;
    default:
      return <span className="inline-flex items-center px-1.5 py-0.5 bg-slate-100 text-slate-600 text-xs font-mono uppercase">{status}</span>;
  }
};

export const AllApplicants = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [minCount, setMinCount] = useState("1");
  const [total, setTotal] = useState(0);
  const [expandedOwner, setExpandedOwner] = useState(null);
  const [page, setPage] = useState(0);
  const limit = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        min_count: minCount,
        limit: limit.toString(),
        skip: (page * limit).toString()
      });
      
      if (searchTerm) {
        params.append("search", searchTerm);
      }
      
      const response = await axios.get(`${API}/ukipo/all-applicants?${params}`);
      setOwners(response.data.owners || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching applicants:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [minCount, searchTerm, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchData();
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ min_count: minCount });
      if (searchTerm) params.append("search", searchTerm);
      
      const response = await axios.get(`${API}/ukipo/all-applicants/export-csv?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_tm_applicants_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const toggleOwnerExpand = (applicant) => {
    setExpandedOwner(expandedOwner === applicant ? null : applicant);
  };

  if (loading && owners.length === 0) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="all-applicants-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="all-applicants-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-slate-900 tracking-tight">
            All TM Applicants
          </h1>
          <p className="text-slate-500 mt-1">
            All trade mark applicants in the UK IPO database, including single-mark owners
          </p>
        </div>
        <Button
          onClick={handleExportCSV}
          disabled={exporting || owners.length === 0}
          className="btn-primary"
          data-testid="export-all-csv-btn"
        >
          {exporting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          EXPORT CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <motion.div 
          className="swiss-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-teal-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-teal-600" />
            </div>
            <span className="text-sm text-slate-500 uppercase tracking-wider">Total Applicants</span>
          </div>
          <p className="stat-value text-teal-600">{total}</p>
          <p className="text-xs text-slate-400 mt-1">with {minCount}+ trade mark{minCount === "1" ? "" : "s"}</p>
        </motion.div>

        <motion.div 
          className="swiss-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-sm text-slate-500 uppercase tracking-wider">Top Applicant</span>
          </div>
          <p className="text-lg font-bold text-slate-900 truncate">
            {owners[0]?.applicant || "—"}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {owners[0]?.trademark_count || 0} trade marks
          </p>
        </motion.div>

        <motion.div 
          className="swiss-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-sm text-slate-500 uppercase tracking-wider">Showing</span>
          </div>
          <p className="stat-value text-emerald-600">{owners.length}</p>
          <p className="text-xs text-slate-400 mt-1">of {total} results</p>
        </motion.div>
      </div>

      {/* Filters */}
      <motion.div 
        className="swiss-card mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-900">Filters</h3>
        </div>
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by applicant name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-swiss pl-12"
              data-testid="search-all-applicants"
            />
          </div>
          <div className="flex gap-2">
            <Select value={minCount} onValueChange={(val) => { setMinCount(val); setPage(0); }}>
              <SelectTrigger className="w-48 input-swiss" data-testid="min-count-filter">
                <SelectValue placeholder="Min trade marks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">All Applicants (1+)</SelectItem>
                <SelectItem value="2">2+ Trade Marks</SelectItem>
                <SelectItem value="3">3+ Trade Marks</SelectItem>
                <SelectItem value="5">5+ Trade Marks</SelectItem>
                <SelectItem value="10">10+ Trade Marks</SelectItem>
                <SelectItem value="20">20+ Trade Marks</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" className="btn-primary" data-testid="apply-filters-btn">
              Apply
            </Button>
          </div>
        </form>
      </motion.div>

      {/* Info Note */}
      <motion.div 
        className="swiss-card bg-teal-50 border-teal-200 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <p className="text-sm text-teal-800">
          <strong>Tip:</strong> This view includes all applicants, including those with a single trade mark registration. 
          Use the filter to narrow down by number of trade marks, or search by applicant name. Export to CSV for outreach campaigns.
        </p>
      </motion.div>

      {/* Results List */}
      <motion.div 
        className="swiss-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : owners.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No applicants found</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="space-y-2">
            {owners.map((owner, index) => (
              <div 
                key={owner.applicant}
                className="border border-slate-200 hover:border-slate-300 transition-colors"
                data-testid={`applicant-row-${index}`}
              >
                <button
                  onClick={() => toggleOwnerExpand(owner.applicant)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-12 h-12 bg-teal-100 flex items-center justify-center font-bold text-teal-600">
                      {index + 1 + page * limit}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900 truncate">{owner.applicant}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 font-mono ${owner.trademark_count === 1 ? 'bg-slate-100 text-slate-600' : 'bg-teal-100 text-teal-700'}`}>
                          {owner.trademark_count} trade mark{owner.trademark_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {expandedOwner === owner.applicant ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </button>

                {expandedOwner === owner.applicant && (
                  <div className="border-t border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">
                      Trade Mark Applications (showing up to 10)
                    </p>
                    <div className="space-y-2">
                      {owner.trademarks.map((tm, tmIndex) => (
                        <div 
                          key={tm.id || tmIndex}
                          className="bg-white p-3 border border-slate-200"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 truncate">{tm.name}</p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                <span className="font-mono">{tm.application_number}</span>
                                <span>{tm.filing_date}</span>
                                {tm.categories && tm.categories.length > 0 && (
                                  <span>Classes: {tm.categories.join(', ')}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {getStatusBadge(tm.status)}
                            </div>
                          </div>
                          {tm.representative_name && (
                            <div className="mt-2 pt-2 border-t border-slate-100">
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-teal-600 font-semibold uppercase">Representative:</span>
                                <span className="text-teal-700 font-medium">{tm.representative_name}</span>
                                {tm.representative_address && (
                                  <span className="text-slate-400 truncate max-w-[300px]" title={tm.representative_address}>
                                    — {tm.representative_address.split(',').slice(0, 2).join(',')}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      {owner.trademark_count > 10 && (
                        <p className="text-xs text-slate-400 text-center pt-2">
                          + {owner.trademark_count - 10} more trade marks (download CSV for complete list)
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AllApplicants;
